import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import api from '../api/client'
import { getNotifications, markNotificationsRead } from '../api/notifications'

export interface Notification {
  id: number
  text: string
  type: 'bonus' | 'topup' | 'info' | 'promo'
  read: boolean
  createdAt: number
  /** Куда ведёт клик по уведомлению (путь на сайте); нет — просто текст. */
  url?: string | null
}

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  addNotification: (text: string, type?: Notification['type']) => void
  markAllRead: () => void
  clearAll: () => void
}

const NotificationContext = createContext<NotificationContextType | null>(null)

const STORAGE_KEY = 'umnik_notifications'
const POLL_MS = 60000
let nextId = Date.now()

function loadFromStorage(): Notification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    /* битый/недоступный localStorage — игнорируем, вернём пустой список */
  }
  return []
}

function saveToStorage(items: Notification[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, 50)))
  } catch {
    /* quota exceeded / приватный режим — тихо пропускаем */
  }
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  // Два источника:
  //   • local  — клиентские transient-уведомления (генерация, топап и т.п.),
  //              мгновенные, кэшируются в localStorage (как и раньше).
  //   • server — персистентные с бэка (движение реф-баланса и пр.), НЕ кэшим
  //              локально (источник правды — БД), гидрируем на mount + polling.
  // serial-id бэка (1,2,3…) и клиентские Date.now()-id (~1.7e12) не пересекаются.
  const [local, setLocal] = useState<Notification[]>(loadFromStorage)
  const [server, setServer] = useState<Notification[]>([])

  useEffect(() => {
    saveToStorage(local)
  }, [local])

  // Гидратация с бэка: на mount + лёгкий polling. Тихо игнорируем ошибки и
  // неавторизованных (на логине уведомлений нет).
  useEffect(() => {
    let alive = true
    let timer: ReturnType<typeof setInterval> | undefined

    const sync = async (): Promise<boolean> => {
      if (!api.isAuthenticated()) return false
      try {
        const res = await getNotifications(50)
        if (!alive) return false
        const list = Array.isArray(res?.notifications) ? res.notifications : []
        setServer(
          list.map((n) => ({
            id: n.id,
            text: n.text,
            type: n.type,
            read: n.read,
            createdAt: n.created_at ? Date.parse(n.created_at) : Date.now(),
            url: n.url ?? null,
          })),
        )
        return true
      } catch {
        // Бэкенд недоступен / эндпоинт ещё не задеплоен (404) — тихо выходим.
        return false
      }
    }

    // Polling запускаем ТОЛЬКО после успешной первой синхронизации — иначе на
    // не-задеплоенном бэке /notifications мы бы спамили 404 каждые POLL_MS.
    void sync().then((ok) => {
      if (ok && alive) timer = setInterval(() => { void sync() }, POLL_MS)
    })

    return () => {
      alive = false
      if (timer) clearInterval(timer)
    }
  }, [])

  // Объединённый список (новые сверху, не больше 50).
  const notifications = useMemo(
    () => [...server, ...local].sort((a, b) => b.createdAt - a.createdAt).slice(0, 50),
    [server, local],
  )

  const addNotification = useCallback((text: string, type: Notification['type'] = 'info') => {
    const n: Notification = { id: ++nextId, text, type, read: false, createdAt: Date.now() }
    setLocal((prev) => [n, ...prev])
  }, [])

  const markAllRead = useCallback(() => {
    setLocal((prev) => prev.map((n) => ({ ...n, read: true })))
    setServer((prev) => prev.map((n) => ({ ...n, read: true })))
    // Помечаем серверные прочитанными (fire-and-forget — UI уже обновлён).
    markNotificationsRead().catch(() => {})
  }, [])

  const clearAll = useCallback(() => {
    // Чистим ТОЛЬКО клиентские transient-уведомления (генерация, топап и т.п.):
    // они и так живут до перезагрузки страницы.
    setLocal([])
    // Серверные (персистентные) НЕ прячем. Раньше здесь стоял setServer([]) —
    // и уведомление о сгорании токенов пропадало из раздела по кнопке
    // «Очистить», возвращаясь только на следующем polling (до 60 с) и уже
    // прочитанным. Для уведомлений о деньгах это выглядит как «мне ничего не
    // приходило». Теперь их только помечаем прочитанными: история остаётся
    // на месте, бейдж обнуляется.
    setServer((prev) => prev.map((n) => ({ ...n, read: true })))
    markNotificationsRead().catch(() => {})
  }, [])

  const unreadCount = notifications.filter((n) => !n.read).length

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, addNotification, markAllRead, clearAll }}>
      {children}
    </NotificationContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider')
  return ctx
}
